import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BrandContainer } from "./BrandContainer";
import { ButtonContainer } from "./ButtonContainer";
import { ImageContainer } from "./Image";
import { ImageSelect } from "./ImageSelect";
import { InputContainer } from "./InputContainer";
import { TableContainer } from "./TableContainer";
import isEqual from "lodash/isEqual";
import { SamplesContext } from "../../context/samplesContext";
import { UserContext } from "../../context/userContext";
import { ActionButtonsPanel } from "./ActionButtonsPanel";
import { SelectedImageInfo } from "./types";
import { ReadOnlyNoticeModal } from "../../components/ReadOnlyNoticeModal";
import { readSeenAdminNoticeSampleId, writeSeenAdminNoticeSampleId } from "./utils/adminNoticeStorage";

export function Home() {
  const { sample, rowSample } = useContext(SamplesContext)
  const { isAuth, isGuest, isAdminMode } = useContext(UserContext)
  const [isUpdate, setIsUpdate] = useState<boolean>(false) // 過去サンプルも紐付けるかどうかのフラグ
  const [manualDrawingIndex, setManualDrawingIndex] = useState<number | null>(null) // テーブルで選択したIndex
  const [selectedImage, setSelectedImage] = useState<SelectedImageInfo | null>(null)
  const [isGuestNoticeOpen, setIsGuestNoticeOpen] = useState(false)
  const [hasShownGuestNotice, setHasShownGuestNotice] = useState(false)
  const [isAdminNoticeOpen, setIsAdminNoticeOpen] = useState(false)

  const sampleImageList = rowSample.sampleImages || []
  const ecImageList = rowSample.sampleEcImages || []
  const defaultSampleImage = useMemo(() => {
    if (sampleImageList.length === 0) return null
    const sorted = [...sampleImageList].sort((a, b) => a.image_index - b.image_index)
    return sorted[0]
  }, [sampleImageList])

  useEffect(() => {
    if (!sample || !rowSample) return

    const hasChanged = !isEqual(sample, rowSample)

    if (hasChanged) {
      const handleBeforeUnload = (event: any) => {
        event.preventDefault()
        event.returnValue = ''
      }

      window.addEventListener('beforeunload', handleBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [sample, rowSample])

  useEffect(() => {
    setSelectedImage(prev => {
      if (defaultSampleImage) {
        return { source: 'sample', image: defaultSampleImage }
      }
      if (ecImageList.length > 0) {
        const sortedEc = [...ecImageList].sort((a, b) => a.image_index - b.image_index)
        return { source: 'ec', image: sortedEc[0] }
      }
      return null
    })
  }, [defaultSampleImage, sampleImageList, ecImageList])

  useEffect(() => {
    if (manualDrawingIndex === null) return
    const targetMeasurement = rowSample.sampleMeasurements.find(
      m => m.measure_index === manualDrawingIndex && !m.is_deleted
    )
    if (targetMeasurement) {
      const matchedImage = rowSample.sampleImages.find(img => img.image_index === targetMeasurement.image_index)
      if (!matchedImage) return
      setSelectedImage(prev => {
        if (prev?.source === 'sample' && prev.image.image_index === matchedImage.image_index) {
          return prev
        }
        return { source: 'sample', image: matchedImage }
      })
      return
    }
    if (defaultSampleImage && selectedImage?.source !== 'sample') {
      setSelectedImage({ source: 'sample', image: defaultSampleImage })
    }
  }, [manualDrawingIndex, rowSample.sampleMeasurements, rowSample.sampleImages, selectedImage, defaultSampleImage])

  useEffect(() => {
    if (!isAuth || !isGuest || hasShownGuestNotice) return
    setIsGuestNoticeOpen(true)
    setHasShownGuestNotice(true)
  }, [isAuth, isGuest, hasShownGuestNotice])

  useEffect(() => {
    if (!isAuth || isGuest || !isAdminMode) return
    const currentSampleId = rowSample?.id
    if (!currentSampleId) return

    const seenSampleId = readSeenAdminNoticeSampleId()
    if (seenSampleId === currentSampleId) return

    setIsAdminNoticeOpen(true)
  }, [isAuth, isGuest, isAdminMode, rowSample?.id])

  const handleAdminNoticeClose = useCallback(() => {
    const currentSampleId = rowSample?.id
    if (currentSampleId) {
      writeSeenAdminNoticeSampleId(currentSampleId)
    }
    setIsAdminNoticeOpen(false)
  }, [rowSample?.id])

  const handleSelectImage = useCallback((source: SelectedImageInfo['source'], image: SelectedImageInfo['image']) => {
    setSelectedImage(prev => {
      if (prev?.source === source && prev.image.image_index === image.image_index) {
        return prev
      }
      return { source, image }
    })
  }, [])

  return (
    <div className="flex flex-col justify-center mt-[12px] mb-[38px] mx-[20px] min-w-[728px] desktop-xmd:mx-[71.5px] desktop-xmd:flex-row">
      <div className="flex flex-col w-full min-w-[226px] mb-[16px] gap-[16px] desktop-xmd:max-w-[300px] desktop-xmd:gap-0 desktop-xmd:mb-0 desktop-xmd:order-3 desktop-xmd:ml-[13px] desktop-xmd:justify-between">
        <InputContainer setIsUpdate={setIsUpdate} />
        <div className="hidden desktop-xmd:block">
          <BrandContainer />
        </div>
        <div className="hidden desktop-xmd:block">
          <ButtonContainer isUpdate={isUpdate} setIsUpdate={setIsUpdate} />
        </div>
      </div>
      <div className="flex flex-col w-full desktop-xmd:order-1 desktop-xmd:mr-[10px] desktop-xmd:max-w-[1000px] desktop-xmd:min-w-[600px]">
        <ImageContainer
          setManualDrawingIndex={setManualDrawingIndex}
          manualDrawingIndex={manualDrawingIndex}
          selectedImage={selectedImage}
        />
        <ImageSelect selectedImage={selectedImage} onSelectImage={handleSelectImage} />
      </div>
      <div className="flex flex-col desktop-xmd:order-2">
        <div className="flex flex-row justify-around gap-[3px] mt-[5px] mb-[30px] desktop-xmd:flex-col desktop-xmd:mt-0 desktop-xmd:mb-0">
          <div className="desktop-xmd:flex-1">
            <TableContainer
              setManualDrawingIndex={setManualDrawingIndex}
              manualDrawingIndex={manualDrawingIndex}
            />
          </div>
          <div className="flex flex-col min-w-[226px] desktop-xmd:hidden">
            <ActionButtonsPanel />
            <BrandContainer />
            <ButtonContainer isUpdate={isUpdate} setIsUpdate={setIsUpdate} />
          </div>
        </div>
        <div className="hidden desktop-xmd:block mt-[12px]">
          <ActionButtonsPanel />
        </div>
      </div>
      <ReadOnlyNoticeModal
        isOpen={isGuestNoticeOpen}
        onClose={() => setIsGuestNoticeOpen(false)}
        message="別部署のサンプルのため、閲覧のみ可能です。"
      />
      <ReadOnlyNoticeModal
        isOpen={isAdminNoticeOpen}
        onClose={handleAdminNoticeClose}
        message="管理者モードのため、閲覧のみ可能です。"
      />
    </div>
  )
}
